// src/services/idleSession.js
// v1.4.1 C7: the idle session timeout on the web — the state the timer
// (component/IdleTimeout.jsx) and the renewal (services/session.js) share.
//
// - Activity is ONLY user input (keyboard, pointer, touch, focus), recorded
//   by IdleTimeout. Polling, websocket and background requests never call
//   markActivity, so a page left open goes idle like any other.
// - The last activity lives in localStorage, so every tab reads the same
//   clock: input in any tab resets every tab. A BroadcastChannel tells the
//   other tabs at once; the `storage` event is the fallback.
// - Every refresh carries `idle_seconds` (session.js asks idleSecondsForRefresh)
//   so the SERVER holds the limit even if a tab keeps polling
//   (apps/accounts/session_limits.py).
// - Sign-out in one tab signs out all: the same channel carries it.
// - An open M-Pesa payment wait defers an idle sign-out until it settles or
//   PAY_WAIT_GRACE_MS pass (beginPayWait / usePayWait).
import axios from 'axios';
import { useEffect } from 'react';
import { API_BASE, SESSION_KEYS, sessionHooks } from './session';

export const ACTIVITY_KEY = 'lpms_last_activity';
export const SIGNOUT_KEY = 'lpms_signout';
export const CHANNEL_NAME = 'lpms-session';
export const WARNING_MS = 60 * 1000;
export const PAY_WAIT_GRACE_MS = 3 * 60 * 1000;
export const DEFAULT_IDLE_MINUTES = 30;
export const IDLE_NOTE = 'Signed out after a period of inactivity.';

export const clock = { now: () => Date.now() };

// ── the shared activity clock ─────────────────────────────────────────────
export function lastActivity() {
  const v = Number(localStorage.getItem(ACTIVITY_KEY));
  return Number.isFinite(v) && v > 0 ? v : null;
}

let channel = null;
function getChannel() {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  try { channel = new BroadcastChannel(CHANNEL_NAME); } catch { channel = null; }
  return channel;
}

function broadcast(message) {
  try { getChannel()?.postMessage(message); } catch { /* the storage event still carries it */ }
}

// Records user input now. Writes at most once a second (pointermove fires a
// lot); `force` for the Stay signed in button.
export function markActivity({ force = false } = {}) {
  const at = clock.now();
  const prev = lastActivity();
  if (!force && prev && at - prev < 1000) return prev;
  localStorage.setItem(ACTIVITY_KEY, String(at));
  broadcast({ type: 'activity', at });
  return at;
}

export function idleMs() {
  const last = lastActivity();
  return last ? Math.max(0, clock.now() - last) : 0;
}

// What a refresh reports: whole seconds since the last input, or null when
// no timer has recorded any (the server then applies the absolute limit only).
// While an M-Pesa wait is open it reports 0: the person is paying, and the
// renewal the wait's polling triggers must not end the session under them
// (the client still ends it PAY_WAIT_GRACE_MS past the limit).
export function idleSecondsForRefresh() {
  const last = lastActivity();
  if (!last) return null;
  if (isPayWaiting()) return 0;
  return Math.floor(Math.max(0, clock.now() - last) / 1000);
}

export function idleLimitMs(user) {
  const minutes = Number(user?.session?.idle_minutes) || DEFAULT_IDLE_MINUTES;
  return minutes * 60 * 1000;
}

// ── the open payment wait ─────────────────────────────────────────────────
// Every tab shares the clock, so every tab must also see a wait open in
// another tab (or an idle tab would sign them all out mid-payment): an open
// wait writes a heartbeat to PAY_WAIT_KEY every few seconds; a heartbeat
// younger than PAY_WAIT_STALE_MS counts as a wait in progress.
export const PAY_WAIT_KEY = 'lpms_pay_wait';
const PAY_WAIT_BEAT_MS = 5 * 1000;
const PAY_WAIT_STALE_MS = 15 * 1000;
let payWaits = 0;
let beat = null;
function writeBeat() { localStorage.setItem(PAY_WAIT_KEY, String(clock.now())); }
export function beginPayWait() {
  payWaits += 1;
  writeBeat();
  if (!beat) beat = setInterval(writeBeat, PAY_WAIT_BEAT_MS);
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    payWaits = Math.max(0, payWaits - 1);
    if (payWaits === 0) {
      clearInterval(beat);
      beat = null;
      localStorage.removeItem(PAY_WAIT_KEY);
    }
  };
}
export function isPayWaiting() {
  if (payWaits > 0) return true;
  const at = Number(localStorage.getItem(PAY_WAIT_KEY));
  return Number.isFinite(at) && at > 0 && clock.now() - at < PAY_WAIT_STALE_MS;
}

// A pay dialog marks its callback wait (STK pushed, polling for the result).
export function usePayWait(active) {
  useEffect(() => (active ? beginPayWait() : undefined), [active]);
}

// ── sign-out, everywhere ──────────────────────────────────────────────────
export const signOutHooks = {
  redirect(path) {
    if (window.location.pathname + window.location.search !== path) window.location.assign(path);
  },
};

function clearSession() {
  Object.values(SESSION_KEYS).forEach((k) => localStorage.removeItem(k));
  localStorage.removeItem(ACTIVITY_KEY);
  localStorage.removeItem(PAY_WAIT_KEY);
}

export function loginPathFor(reason) {
  return reason === 'idle' ? '/login?signed_out=idle' : '/login';
}

// Ends the session the way session.js endSession does (the session keys
// gone, /login), tells the server (the refresh token is blacklisted — an idle
// sign-out is also audited there), and tells every other tab.
export async function signOut(reason = 'user', { redirect = true } = {}) {
  const refresh = localStorage.getItem(SESSION_KEYS.refresh);
  clearSession();
  localStorage.setItem(SIGNOUT_KEY, JSON.stringify({ reason, at: clock.now() }));
  broadcast({ type: 'signout', reason });
  if (refresh) {
    try {
      // Plain axios: never through the renewal interceptors.
      await axios.post(`${API_BASE}/accounts/logout/`, { refresh, ...(reason === 'idle' ? { reason } : {}) });
    } catch { /* the keys are gone either way; the token dies at its expiry */ }
  }
  if (redirect) signOutHooks.redirect(loginPathFor(reason));
}

// Another tab signed out: this one follows, without asking the server again.
export function followSignOut(reason) {
  clearSession();
  signOutHooks.redirect(loginPathFor(reason));
}

// Listens to the other tabs: activity (re-check the clock) and sign-out.
// Returns the unsubscribe function.
export function subscribe({ onActivity, onSignOut }) {
  const ch = getChannel();
  const onMessage = (e) => {
    const m = e?.data || {};
    if (m.type === 'activity') onActivity?.(m.at);
    if (m.type === 'signout') onSignOut?.(m.reason);
  };
  const onStorage = (e) => {
    if (e.key === ACTIVITY_KEY && e.newValue) onActivity?.(Number(e.newValue));
    if (e.key === SIGNOUT_KEY && e.newValue) {
      let reason = 'user';
      try { reason = JSON.parse(e.newValue).reason || 'user'; } catch { /* default */ }
      onSignOut?.(reason);
    }
  };
  ch?.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);
  return () => {
    ch?.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };
}

// The renewal reports idle_seconds (installed once, here, so session.js
// does not import this module).
sessionHooks.idleSeconds = idleSecondsForRefresh;

// Tests: forget the channel and the pay waits.
export function _resetForTests() {
  try { channel?.close(); } catch { /* ignore */ }
  channel = null;
  payWaits = 0;
  clearInterval(beat);
  beat = null;
}
